/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx, keyframes } from '@emotion/core';
import { Input, Spin, Empty, Typography, Button } from 'antd';
import { Fragment, useMemo, useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useProjectId } from '~src/hooks/useProjectId';
import { useTemplateArchiveFolderStructure } from '~src/hooks/api-hooks/useProjectBuilderModal';
import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';
import { arrToTree, TableEntry } from '~src/utils/securityGroupsConfig';
import { CheckboxFolderTree } from '../components/CheckboxFolderTree';
import IconButton from '~components/_common/IconButton/IconButton';

const { Title } = Typography;

interface FolderSelectionStepProps {
  templateId: string | null;
  selectedFolderIds: Set<number>;
  onToggleFolderSelection: (folderId: number, isChecked: boolean) => void;
}

const filterFolderTree = (
  folders: TableEntry[],
  searchTerm: string
): TableEntry[] => {
  if (!searchTerm) return folders;

  const lowerSearch = searchTerm.toLowerCase();

  const filterRecursive = (folder: TableEntry): TableEntry | null => {
    const nameMatch = folder.title.toLowerCase().includes(lowerSearch);
    const descMatch = folder.dbFolder.description
      ?.toLowerCase()
      .includes(lowerSearch);

    let filteredChildren: TableEntry[] = [];
    if (folder.children) {
      filteredChildren = folder.children
        .map((child) => filterRecursive(child))
        .filter((child): child is TableEntry => child !== null);
    }

    if (nameMatch || descMatch || filteredChildren.length > 0) {
      return {
        ...folder,
        children:
          filteredChildren.length > 0 ? filteredChildren : folder.children,
      };
    }

    return null;
  };

  return folders
    .map((folder) => filterRecursive(folder))
    .filter((folder): folder is TableEntry => folder !== null);
};

export const FolderSelectionStep: React.FC<FolderSelectionStepProps> = ({
  templateId,
  selectedFolderIds,
  onToggleFolderSelection,
}) => {
  const intl = useIntl();
  const styles = useStyles(styleFn);
  const projectId = useProjectId();
  const [searchText, setSearchText] = useState('');
  const [expandedFolderIds, setExpandedFolderIds] = useState<Set<number>>(
    new Set()
  );

  const templateFoldersQuery = useTemplateArchiveFolderStructure(
    projectId!,
    templateId,
    !!templateId
  );

  const folderTree = useMemo<TableEntry[]>(() => {
    if (!templateFoldersQuery.data) return [];
    const sortedFolders = [...templateFoldersQuery.data].sort((a, b) =>
      a.name.localeCompare(b.name)
    );
    return arrToTree(sortedFolders, null);
  }, [templateFoldersQuery.data]);

  // Get all folder IDs recursively
  const getAllFolderIds = (folders: TableEntry[]): number[] => {
    const ids: number[] = [];
    const collectIds = (folderList: TableEntry[]) => {
      folderList.forEach((folder) => {
        ids.push(folder.dbFolder.id);
        if (folder.children && folder.children.length > 0) {
          collectIds(folder.children);
        }
      });
    };
    collectIds(folders);
    return ids;
  };

  // Initialize with all folders expanded
  useEffect(() => {
    if (folderTree.length > 0 && expandedFolderIds.size === 0) {
      const allIds = getAllFolderIds(folderTree);
      setExpandedFolderIds(new Set(allIds));
    }
  }, [folderTree]);

  const filteredFolderTree = useMemo(() => {
    return filterFolderTree(folderTree, searchText);
  }, [folderTree, searchText]);

  // Handle expand all
  const handleExpandAll = () => {
    const allIds = getAllFolderIds(folderTree);
    setExpandedFolderIds(new Set(allIds));
  };

  // Handle collapse all
  const handleCollapseAll = () => {
    setExpandedFolderIds(new Set());
  };

  // Handle select all
  const handleSelectAll = () => {
    const allIds = getAllFolderIds(filteredFolderTree);
    allIds.forEach((id) => {
      if (!selectedFolderIds.has(id)) {
        onToggleFolderSelection(id, true);
      }
    });
  };

  // Handle clear selection
  const handleClearSelection = () => {
    const allIds = getAllFolderIds(filteredFolderTree);
    allIds.forEach((id) => {
      if (selectedFolderIds.has(id)) {
        onToggleFolderSelection(id, false);
      }
    });
  };

  // Handle toggle expand
  const handleToggleExpand = (folderId: number) => {
    setExpandedFolderIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(folderId)) {
        newSet.delete(folderId);
      } else {
        newSet.add(folderId);
      }
      return newSet;
    });
  };

  if (templateFoldersQuery.isLoading) {
    return (
      <div css={styles.loadingContainer}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div css={styles.container}>
      <div css={styles.titleRow}>
        <Title level={4} css={styles.sectionTitle}>
          {intl.formatMessage({ id: 'select-folders' })}
        </Title>
      </div>

      {templateFoldersQuery.isError ? (
        <Empty
          description={intl.formatMessage({
            id: 'failed-to-load-template-folders',
          })}
        />
      ) : folderTree.length === 0 ? (
        <Empty
          description={intl.formatMessage({ id: 'no-template-folders' })}
        />
      ) : (
        <Fragment>
          <Input
            prefix={<IconButton iconStyle="far" icon="search" backgroundOff />}
            size="small"
            placeholder={intl.formatMessage({ id: 'search-folders' })}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            css={styles.searchInput}
          />

          <div css={styles.buttonRow}>
            <div css={styles.leftButtons}>
              <Button
                size="small"
                onClick={handleSelectAll}
                css={styles.actionButton}>
                {intl.formatMessage({ id: 'select-all-folders' })}
              </Button>
              <Button
                size="small"
                onClick={handleClearSelection}
                css={styles.actionButton}>
                {intl.formatMessage({ id: 'clear-selection' })}
              </Button>
            </div>
            <div css={styles.rightButtons}>
              <Button
                size="small"
                onClick={handleExpandAll}
                css={styles.actionButton}>
                {intl.formatMessage({ id: 'expand-all' })}
              </Button>
              <Button
                size="small"
                onClick={handleCollapseAll}
                css={styles.actionButton}>
                {intl.formatMessage({ id: 'collapse-all' })}
              </Button>
            </div>
          </div>

          {filteredFolderTree.length === 0 ? (
            <Empty
              description={intl.formatMessage({
                id: 'no-folders-match-search',
              })}
              css={styles.emptySearch}
            />
          ) : (
            <CheckboxFolderTree
              folders={filteredFolderTree}
              selectedIds={selectedFolderIds}
              onToggle={onToggleFolderSelection}
              expandedFolderIds={expandedFolderIds}
              onToggleExpand={handleToggleExpand}
            />
          )}
        </Fragment>
      )}
    </div>
  );
};

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const styleFn: ThemedStyleFn = (theme) => ({
  container: {
    animation: `${fadeIn} 0.4s ease-out`,
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 400,
  },
  titleRow: {
    marginBottom: 16,
  },
  sectionTitle: {
    fontSize: 16,
    marginBottom: 0,
    color: theme.colors.text.primary,
    fontWeight: 600,
  },
  buttonRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  leftButtons: {
    display: 'flex',
    gap: 8,
  },
  rightButtons: {
    display: 'flex',
    gap: 8,
  },
  actionButton: {
    borderRadius: 6,
    fontWeight: 500,
    fontSize: 13,
    height: 30,
    padding: '0 12px',
    border: `1px solid ${theme.colors.border}`,
    color: theme.colors.text.primary,
    background: '#ffffff',
    transition: 'all 0.15s ease',
    '&:hover': {
      borderColor: theme.colors.primary,
      color: theme.colors.primary,
    },
  },
  searchInput: {
    marginBottom: 16,
    '.ant-input': {
      backgroundColor: theme.colors.backgroundSecondary,
    },
  },
  emptySearch: {
    padding: '40px 0',
  },
});
